import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, catchError, finalize, of, switchMap, takeUntil, tap } from 'rxjs';
import {
  BULK_SCRAP_STATUSES,
  BulkScrapRequestDto,
  BulkScrapRequestService,
  BulkScrapStatus,
} from '../../services/bulk-scrap-request.service';
import { CC_PERMISSIONS, PermissionService } from '../../services/permission.service';
import { PaginationComponent } from '../shared/pagination/pagination.component';
import { BULK_SCRAP_STATUS_META } from './bulk-scrap-display';
import {
  BulkScrapSourceBadgeComponent,
  BulkScrapStatusBadgeComponent,
} from './bulk-scrap-badges.component';
import { BulkScrapStatusEditorComponent } from './bulk-scrap-status-editor.component';
import {
  BulkScrapRequestDetailsComponent,
  PhotoOpenEvent,
} from './bulk-scrap-request-details.component';

@Component({
  selector: 'app-bulk-scrap-requests-list',
  standalone: true,
  imports: [
    CommonModule,
    PaginationComponent,
    BulkScrapStatusBadgeComponent,
    BulkScrapSourceBadgeComponent,
    BulkScrapStatusEditorComponent,
    BulkScrapRequestDetailsComponent,
  ],
  templateUrl: './bulk-scrap-requests-list.component.html',
})
export class BulkScrapRequestsListComponent implements OnInit, OnDestroy {
  private service = inject(BulkScrapRequestService);
  private permissions = inject(PermissionService);
  private destroy$ = new Subject<void>();
  private reload$ = new Subject<void>();
  private successTimer: ReturnType<typeof setTimeout> | null = null;
  private photoTrigger: HTMLElement | null = null;
  private drawerTrigger: HTMLElement | null = null;

  @ViewChild('photoCloseButton') photoCloseButton?: ElementRef<HTMLButtonElement>;

  readonly statusOptions = BULK_SCRAP_STATUSES;
  readonly statusMeta = BULK_SCRAP_STATUS_META;

  requests: BulkScrapRequestDto[] = [];
  totalCount = 0;
  pageIndex = 0;
  pageSize = 20;
  selectedStatus: BulkScrapStatus | '' = '';
  isLoading = false;
  errorMessage = '';
  successMessage = '';

  editingRequestId: string | null = null;
  selectedRequest: BulkScrapRequestDto | null = null;
  previewPhotoUrl: string | null = null;

  /** Marketing can view; only ops roles (Appointments_Manage) can change status. */
  get canManageStatus(): boolean {
    return this.permissions.hasAny(
      CC_PERMISSIONS.APPOINTMENTS_MANAGE,
      CC_PERMISSIONS.IS_TELEMARKETER,
      CC_PERMISSIONS.IS_CC_ADMIN
    );
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
  }

  get emptyMessage(): string {
    return this.selectedStatus
      ? `No inquiries with status "${this.statusMeta[this.selectedStatus].label}".`
      : 'No bulk scrap inquiries have been received yet.';
  }

  ngOnInit(): void {
    // A single switchMap stream cancels superseded requests, so a slow response
    // for an older filter/page can never overwrite newer results.
    this.reload$
      .pipe(
        tap(() => {
          this.isLoading = true;
          this.errorMessage = '';
        }),
        switchMap(() =>
          this.service.getList(this.pageIndex, this.pageSize, this.selectedStatus || undefined).pipe(
            catchError((err) => {
              this.errorMessage = err?.error?.message || 'Failed to load bulk scrap requests.';
              return of(null);
            }),
            finalize(() => (this.isLoading = false))
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.totalCount = res.totalCount || 0;
        // The result set can shrink (status change, other users); clamp to the
        // last valid page instead of showing an empty page with no pagination.
        if (this.pageIndex > 0 && this.pageIndex >= this.totalPages) {
          this.pageIndex = this.totalPages - 1;
          this.reload$.next();
          return;
        }
        this.requests = res.items || [];
      });

    this.loadRequests();
  }

  ngOnDestroy(): void {
    this.clearSuccessTimer();
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadRequests(): void {
    this.reload$.next();
  }

  onStatusFilterChange(status: BulkScrapStatus | ''): void {
    this.selectedStatus = status;
    this.pageIndex = 0;
    this.editingRequestId = null;
    this.loadRequests();
  }

  onPageChange(page: number): void {
    this.pageIndex = Math.max(0, page - 1);
    this.editingRequestId = null;
    this.loadRequests();
  }

  onStatusSaved(req: BulkScrapRequestDto, updated: BulkScrapRequestDto): void {
    Object.assign(req, updated);
    this.editingRequestId = null;
    this.showSuccess(`Status updated to ${this.statusMeta[updated.status].label}`);
    // The request no longer matches the active filter; refresh the page.
    if (this.selectedStatus && updated.status !== this.selectedStatus) {
      this.loadRequests();
    }
  }

  openDetails(req: BulkScrapRequestDto, event: Event): void {
    this.drawerTrigger = event.currentTarget as HTMLElement;
    this.editingRequestId = null;
    this.selectedRequest = req;
  }

  closeDetails(): void {
    this.selectedRequest = null;
    this.restoreFocus(this.drawerTrigger);
    this.drawerTrigger = null;
  }

  dismissSuccess(): void {
    this.clearSuccessTimer();
    this.successMessage = '';
  }

  openPhoto(url: string, trigger: HTMLElement): void {
    this.photoTrigger = trigger;
    this.previewPhotoUrl = url;
    setTimeout(() => this.photoCloseButton?.nativeElement.focus());
  }

  onDrawerPhoto(event: PhotoOpenEvent): void {
    this.openPhoto(event.url, event.trigger);
  }

  closePhoto(): void {
    this.previewPhotoUrl = null;
    this.restoreFocus(this.photoTrigger);
    this.photoTrigger = null;
  }

  /** Escape closes the top-most layer: photo viewer first, then the drawer. */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.previewPhotoUrl) {
      this.closePhoto();
    } else if (this.selectedRequest) {
      this.closeDetails();
    }
  }

  trackById(_: number, req: BulkScrapRequestDto): string {
    return req.id;
  }

  private showSuccess(text: string): void {
    // Cancel any earlier timer so it cannot clear this newer message early.
    this.clearSuccessTimer();
    this.successMessage = text;
    this.successTimer = setTimeout(() => {
      this.successMessage = '';
      this.successTimer = null;
    }, 3000);
  }

  private clearSuccessTimer(): void {
    if (this.successTimer) {
      clearTimeout(this.successTimer);
      this.successTimer = null;
    }
  }

  private restoreFocus(el: HTMLElement | null): void {
    setTimeout(() => el?.isConnected && el.focus());
  }
}
